/**
 * Sanitiert Signatur-HTML im Add-in mit DOMPurify (Defense in Depth).
 * Erlaubt nur Tags/Attribute, die der Editor erzeugen kann;
 * img-src: http(s), data:image oder cid:sunnysign-…;
 * href: http(s), mailto: oder tel:.
 */

import DOMPurify, { type Config } from "dompurify";

/** Tags, die der Signatur-Editor erzeugen kann (Font, Größe, Farbe, Fett/Kursiv/Unterstrichen, Link, Bild, HR, Tabellen, Profilfelder). */
const ALLOWED_TAGS = [
  "a",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "span",
  "div",
  "p",
  "br",
  "img",
  "hr",
  "ul",
  "ol",
  "li",
  "font", // Legacy (z. B. execCommand fontName)
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "colgroup",
  "col",
  "caption",
];

/** Attribute, die der Editor nutzt (style, href, src, width, height, class, Tabellen-Attribute, etc.). */
const ALLOWED_ATTR = [
  "href",
  "target",
  "style",
  "src",
  "alt",
  "width",
  "height",
  "class",
  "size",
  "face",
  "color",
  // Tabellen: von E-Mail-Clients (v. a. Outlook Desktop) zusätzlich zum CSS ausgewertet
  "border",
  "cellpadding",
  "cellspacing",
  "colspan",
  "rowspan",
  "align",
  "valign",
  "bgcolor",
  "span",
  "data-sunnysign-asset",
  "data-sunnysign-filename",
  "data-sunnysign-ctype",
];

/** http(s), mailto, tel, data:image, cid:sunnysign-… für Inline-Attachments. */
const ALLOWED_URI_REGEXP =
  /^(?:https?:|mailto:|tel:|data:image\/(?:png|jpe?g|gif|webp);base64,|cid:sunnysign-)/i;

/** Erlaubte Link-Schemes im Editor (ohne Bild-URIs). */
const ALLOWED_HREF_REGEXP = /^(?:https?:|mailto:|tel:)/i;

const DATA_IMAGE_SRC_FULL =
  /^data:image\/(?:png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=\s]+$/i;

const CID_SRC_FULL =
  /^cid:sunnysign-[0-9a-f-]{32,36}\.(?:png|jpe?g|gif|webp)$/i;

/** Erlaubte Werte der Attachment-Marker (Asset-ID, Blob-Dateiname, MIME-Typ). */
const ASSET_MARKER_VALUE = new Map<string, RegExp>([
  ["data-sunnysign-asset", /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i],
  ["data-sunnysign-filename", /^sunnysign-[0-9a-f-]{32,36}\.(?:png|jpe?g|gif|webp)$/i],
  ["data-sunnysign-ctype", /^image\/(?:png|jpeg|gif|webp)$/i],
]);

/** CSS-Eigenschaften, die der Editor setzen kann (Farbe, Schrift, Ausrichtung, Tabellen, Abstände). */
const ALLOWED_CSS_PROPERTIES = [
  "color",
  "background-color",
  "background",
  "font-size",
  "font-family",
  "font-weight",
  "font-style",
  "text-align",
  "text-decoration",
  "text-decoration-line",
  "text-decoration-color",
  "text-decoration-style",
  "width",
  "height",
  "max-width",
  "min-width",
  // Tabellen und Zellen
  "border",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "border-width",
  "border-style",
  "border-color",
  "border-top-width",
  "border-top-style",
  "border-top-color",
  "border-right-width",
  "border-right-style",
  "border-right-color",
  "border-bottom-width",
  "border-bottom-style",
  "border-bottom-color",
  "border-left-width",
  "border-left-style",
  "border-left-color",
  "border-radius",
  "border-collapse",
  "border-spacing",
  "table-layout",
  "vertical-align",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "line-height",
  "white-space",
  "display",
  "float",
];

/** Werte mit URL-/Ausdrucks-Funktionen sind nie nötig und werden verworfen. */
const UNSAFE_CSS_VALUE = /(?:url\s*\(|expression\s*\(|javascript\s*:|@import)/i;

export type SanitizeSignatureResult = {
  html: string;
  /** True, wenn href/src o. Ä. wegen unerlaubtem Schema entfernt wurden. */
  blocked: boolean;
};

/** Ob die URL als Signatur-Link erlaubt ist (http(s), mailto, tel). */
export function isAllowedLinkHref(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  return ALLOWED_HREF_REGEXP.test(trimmed);
}

function normalizeCssProp(prop: string): string {
  return prop.replace(/([A-Z])/g, "-$1").toLowerCase();
}

/** Splittet CSS-Deklarationen, ohne Semikolons innerhalb von Anführungszeichen zu trennen (z. B. font-family: "Times New Roman", serif). */
function splitStyleDeclarations(styleValue: string): string[] {
  const out: string[] = [];
  let start = 0;
  let inQuote = "";
  for (let i = 0; i < styleValue.length; i++) {
    const c = styleValue[i];
    if (c === '"' || c === "'") {
      if (!inQuote) inQuote = c;
      else if (c === inQuote) inQuote = "";
    } else if (c === ";" && !inQuote) {
      out.push(styleValue.slice(start, i).trim());
      start = i + 1;
    }
  }
  if (start < styleValue.length) {
    const tail = styleValue.slice(start).trim();
    if (tail) out.push(tail);
  }
  return out;
}

/** Filtert einen style-String auf erlaubte CSS-Eigenschaften. Erhält font-family (auch mit Kommas/Anführungszeichen). */
function filterStyleString(styleValue: string): string {
  if (!styleValue || typeof styleValue !== "string") return "";
  const out: string[] = [];
  const decls = splitStyleDeclarations(styleValue);
  for (const decl of decls) {
    const colon = decl.indexOf(":");
    if (colon <= 0) continue;
    const prop = normalizeCssProp(decl.slice(0, colon).trim());
    const val = decl.slice(colon + 1).trim();
    if (!val || UNSAFE_CSS_VALUE.test(val)) continue;
    if (ALLOWED_CSS_PROPERTIES.includes(prop)) out.push(`${prop}:${val}`);
  }
  return out.length ? out.join(";") + ";" : "";
}

/** Pro Sanitize-Lauf: wurde ein gefährliches href/src verworfen? */
let blockedDuringSanitize = false;

/** Hooks: Style filtern; href nur http(s)/mailto/tel; data: nur als img-src. */
function applySanitizeHooks(): void {
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const raw =
        (node as Element).getAttribute?.("style") ?? data.attrValue ?? "";
      data.attrValue = filterStyleString(raw);
      return;
    }
    const val = (data.attrValue ?? "").trim();
    const markerPattern = ASSET_MARKER_VALUE.get(data.attrName);
    if (markerPattern) {
      data.keepAttr = markerPattern.test(val);
      return;
    }
    if (data.attrName === "href") {
      if (!val || !ALLOWED_HREF_REGEXP.test(val)) {
        data.keepAttr = false;
        if (val) blockedDuringSanitize = true;
      }
      return;
    }
    if (data.attrName === "src" && /^data:/i.test(val)) {
      const compact = val.replace(/\s+/g, "");
      if (!DATA_IMAGE_SRC_FULL.test(compact)) {
        data.keepAttr = false;
        blockedDuringSanitize = true;
      } else {
        data.attrValue = compact;
      }
      return;
    }
    if (data.attrName === "src" && /^cid:/i.test(val)) {
      if (!CID_SRC_FULL.test(val.trim())) {
        data.keepAttr = false;
        blockedDuringSanitize = true;
      }
      return;
    }
    if (data.attrName === "src" && val && !ALLOWED_URI_REGEXP.test(val)) {
      data.keepAttr = false;
      blockedDuringSanitize = true;
    }
  });
}

const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  // Werte sind keine URIs — ohne URI_SAFE prüft DOMPurify sie gegen ALLOWED_URI_REGEXP und streicht das Attribut
  // (betraf Legacy <font color|face|size>, Bildmaße, Tabellen-Attribute und die Attachment-Marker;
  // letztere werden im Hook validiert).
  ADD_URI_SAFE_ATTR: [
    "color",
    "face",
    "size",
    "width",
    "height",
    "border",
    "cellpadding",
    "cellspacing",
    "colspan",
    "rowspan",
    "align",
    "valign",
    "bgcolor",
    "span",
    "data-sunnysign-asset",
    "data-sunnysign-filename",
    "data-sunnysign-ctype",
  ],
  ALLOW_DATA_ATTR: false,
};

let hookRegistered = false;

/**
 * Bereinigt Signatur-HTML und meldet, ob unerlaubte URIs entfernt wurden.
 */
export function sanitizeSignatureHtmlResult(html: string): SanitizeSignatureResult {
  if (typeof window === "undefined") return { html: html || "", blocked: false };
  if (!html) return { html: "", blocked: false };
  if (!hookRegistered) {
    applySanitizeHooks();
    hookRegistered = true;
  }
  blockedDuringSanitize = false;
  const out = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  return { html: out, blocked: blockedDuringSanitize };
}

/**
 * Bereinigt Signatur-HTML für Anzeige und vor dem Senden an die API.
 * Erhält alle Editor-Funktionen (Formatierung, Links, Bilder, Platzhalter wie {{Vorname}}).
 */
export function sanitizeSignatureHtml(html: string): string {
  return sanitizeSignatureHtmlResult(html).html;
}
